import { Reveal } from "kido/reveal";

import { App } from "@app/context";
import { AboutPage } from "@app/pages/about";
import { HomePage } from "@app/pages/home";
import { PlayPage } from "@app/pages/play";
import { PlayDetailPage } from "@app/pages/play-detail";
import { ProjectPage } from "@app/pages/project";
import { ProjectsPage } from "@app/pages/projects";
import { BasePage, type PageContext } from "@app/primitives/base-page";

type PageConstructor = new (ctx?: PageContext) => BasePage;
type PagePattern = { test: (key: string) => boolean; cls: PageConstructor };
type ScrollSnapshot = { cur: number; tar: number };
type CurrentPage = { key: string; instance: BasePage };

class PageManagerImpl {
  private readonly registry: Map<string, PageConstructor>;
  private readonly patterns: PagePattern[];
  private current: CurrentPage | null;
  private readonly scrollState: Record<string, ScrollSnapshot>;
  private reveal: Reveal = new Reveal();
  private scrollerUnsub: null | (() => void) = null;

  constructor() {
    this.registry = new Map();
    this.patterns = [];
    this.current = null;
    this.scrollState = Object.create(null);

    this.register("home", HomePage);
    this.register("projects", ProjectsPage);
    this.register("project", ProjectPage);
    this.register("play", PlayPage);
    this.register("play-detail", PlayDetailPage);
    this.register("about", AboutPage);
  }

  register(key: string, PageClass: PageConstructor): this {
    if (key && PageClass) this.registry.set(key, PageClass);
    return this;
  }

  registerPattern(
    testFn: (key: string) => boolean,
    PageClass: PageConstructor
  ): this {
    if (typeof testFn === "function" && PageClass)
      this.patterns.push({ test: testFn, cls: PageClass });
    return this;
  }

  resolve(key?: string | null): PageConstructor | null {
    if (!key) return null;
    if (this.registry.has(key)) return this.registry.get(key) ?? null;
    const pattern = this.patterns.find((p) => p.test(key));
    return pattern ? pattern.cls : null;
  }

  ensure(key?: string | null): BasePage {
    const PageCls = this.resolve(key) || BasePage;
    return new PageCls({ key, App });
  }

  getPageClass(key?: string | null): PageConstructor | null {
    return this.resolve(key);
  }

  getCurrent(): CurrentPage | null {
    return this.current;
  }

  getCurrentInstance(): BasePage | null {
    return this.current?.instance ?? null;
  }

  getCurrentSections(): HTMLElement[] {
    const instance = this.current?.instance;
    return instance?.getSections?.() ?? [];
  }

  async beforeOut(): Promise<void> {
    try {
      const prevKey = App.route?.old?.page || null;
      if (!prevKey) return;

      if (this.current && this.current.key === prevKey) {
        try {
          const sc = (this.current.instance as any)?.scroller;
          if (sc) this.scrollState[prevKey] = { cur: sc.cur, tar: sc.tar };
        } catch {}

        try {
          if (this.scrollerUnsub) this.scrollerUnsub();
        } catch {}
        this.scrollerUnsub = null;

        try {
          this.reveal.destroy();
        } catch {}

        try {
          App.lazyVideo?.reset();
        } catch {}

        await (this.current.instance?.out?.() || Promise.resolve());
      } else {
        const tmp = this.ensure(prevKey);
        await (tmp.out?.() || Promise.resolve());
      }
    } catch (e) {
      console.warn("[page:out] error", e);
    }
  }

  async initPage(): Promise<void> {
    try {
      const nextKey = App.route?.new?.page || null;
      if (!nextKey) return;

      const instance = this.ensure(nextKey);
      this.current = { key: nextKey, instance };

      try {
        const container =
          (App.container as HTMLElement | null) ||
          (document.getElementById("page") as HTMLElement | null);

        this.reveal.init({
          root: container,
          selectors: {
            split: "._s",
          },
          getScrollPosition: () => App.scroller?.current ?? 0,
        });
        await instance.init(container);

        // Scan the new page for lazy videos after page init finishes
        // (init may inject template content async).
        App.lazyVideo?.observe(container);
      } catch {}
    } catch (e) {
      console.warn("[page:init] error", e);
    }
  }

  async pageIn(): Promise<void> {
    try {
      const instance = this.current?.instance;
      if (!instance) return;

      await (instance.in?.() || Promise.resolve());

      try {
        if (App.scroller) {
          if (this.scrollerUnsub) this.scrollerUnsub();
          this.scrollerUnsub = App.scroller.subscribe((e) => {
            try {
              (instance as any)?.onScroll?.(e);
            } catch {}
          });
        }
      } catch {}
    } catch (e) {
      console.warn("[page:in] error", e);
    }
  }

  async afterIn(): Promise<void> {
    await this.initPage();
    await this.pageIn();
  }

  registerFromRoutes(): void {
    try {
      const map = App.config?.routes || {};
      Object.values(map).forEach((key) => {
        if (!key) return;
        if (!this.resolve(key)) this.register(key, BasePage);
      });
    } catch {}
  }

  getScroll(key: string | null | undefined): ScrollSnapshot {
    return (key && this.scrollState[key]) || { cur: 0, tar: 0 };
  }

  setScroll(
    key: string | null | undefined,
    value?: { cur?: number; tar?: number }
  ): void {
    if (key && value) {
      this.scrollState[key] = {
        cur: Number(value.cur) || 0,
        tar: Number(value.tar) || 0,
      };
    }
  }
}

export const PageManager = new PageManagerImpl();
App.pageManager = PageManager;
export default PageManager;
